<template>
    <div class="flex w-full flex-col dashboard-settings">
        <div class="w-full flex flex-col relative">
            <NavBar/>
            <div class="w-full px-4 2xl:px-40 3xl:px-64 under-header-block">
                <slot>
                    <div class="items-center flex justify-between md:h-23-5">
                        <div class="flex items-center xl:-mx-24 w-full">
                            <div @click="onDiscard"
                                 class="col-span-1 flex items-center text-primary hover:text-primary cursor-pointer">
                                <IconDirLeft/>
                                <span class="mx-1">{{ $t('general.back') }}</span>
                            </div>
                            <span class="mx-4 lg:mx-8">
                            <svg width="1" height="88" viewBox="0 0 1 88" fill="none"
                                 xmlns="http://www.w3.org/2000/svg">
                                <rect width="1" height="88" fill="#EBEBEB"/>
                            </svg>
                            </span>
                            <div class="flex flex-col w-full flex-1">
                                <span class="text-xl font-bold main-text-color">
                                    {{ $t('dashboard.dashboardSettings') }}
                                </span>
                                <div class="flex justify-center items-center w-full md:hidden flex-col">
                                    <base-button type="danger"
                                                 outline
                                                 square
                                                 size="sm"
                                                 @click="onDelete">
                                        <template v-slot:icon>
                                            <IconDelete />
                                        </template>
                                    </base-button>
                                    <CopyDashboard class="mx-4"
                                                   :dashboard="activeDashboard"/>
                                </div>
                            </div>
                        </div>
                        <div class="flex-row items-center hidden md:flex">
                            <base-button type="danger"
                                         outline
                                         square
                                         size="md"
                                         @click="onDelete">
                                <template v-slot:icon>
                                    <IconDelete />
                                </template>
                            </base-button>
                            <CopyDashboard class="mx-4"
                                           :dashboard="activeDashboard"/>
                        </div>
                    </div>
                </slot>
            </div>
            <div class="w-full px-4 mt-1px pt-6 lg:px-16 2xl:px-40 3xl:px-64 main-bg-dark">
                <DashboardSettings
                    v-model="model"
                    @on-update-groups="onUpdateGroups"
                    @on-change-layout="onChangeLayout"
                    :current-dashboard="currentDashboard"
                />
            </div>
        </div>
        <ConfirmDialog :visible.sync="showConfirmDialog"
                       @on-cancel="showConfirmDialog = false"
                       @on-confirm="deleteDashboard"
        />
    </div>
</template>
<script>
    import get from 'lodash/get'
    import { Popover } from 'element-ui'
    import cloneDeep from 'lodash/cloneDeep'
    export default {
        components: {
            NavBar: () => import('@/views/common/NavBar'),
            DeleteLayout: () => import('@/views/common/DeleteLayout'),
            CopyDashboard: () => import('@/views/DashboardSettings/sections/CopyDashboard'),
            ColorParameterType: () => import('@/views/DashboardSettings/LayoutManagement/components/ColorParameterType'),
            DashboardSettings: () => import('@/views/DashboardSettings/sections/DashboardSettings'),
            [Popover.name]: Popover,
            ConfirmDialog: () => import('@/components/Common/ConfirmDialog'),
            CancelButton: () => import("@/components/Common/Buttons/CancelButton"),
            ConfirmButton: () => import("@/components/Common/Buttons/ConfirmButton")
        },
        data() {
            return {
                model: {},
                currentDashboard: {},
                loading: false,
                initialLayout: {},
                showConfirmDialog: false
            }
        },
        computed: {
            activeDashboard() {
                return this.$store.getters['dashboards/getActiveDashboard']
            },
            activeLayout() {
                return this.$store.getters['layout/getActiveLayout']
            },
            getActiveDashboardLayoutID() {
                return get(this.$store.getters['dashboards/getActiveDashboard'], 'DashboardLayoutID', 1)
            },
            storedDashboardLayout() {
                return this.$store.getters['layout/storedDashboardLayout'](this.getActiveDashboardLayoutID)
            },
            allDashboards() {
                return this.$store.getters['dashboards/getAllDashboards']
            }
        },
        methods: {
            async onDiscard(goBack = true) {
                if (goBack) {
                    await this.$store.dispatch('dashboards/getDashboards')
                    await this.$router.push('/')
                    return
                }
                this.discardLayoutChanges()
                this.discardGroupsManagement()
            },
            discardLayoutChanges() {
                this.$store.dispatch('layout/updateActiveLayout', this.initialLayout)
            },
            discardGroupsManagement() {
                this.model = cloneDeep(this.$store.state.dashboards.activeDashboard)
                this.currentDashboard = cloneDeep(this.$store.state.dashboards.activeDashboard)
            },
            async deleteDashboard() {
                try {
                    this.loading = true
                    await this.$store.dispatch('dashboards/deleteDashboard', this.currentDashboard)
                    if (!this.allDashboards.length) {
                        await this.$router.push('/')
                        localStorage.clear()
                        setTimeout(() => {
                            window.location.reload()
                        }, 200)
                        return
                    }
                    await this.$store.dispatch('dashboards/selectDashboard', this.allDashboards[this.allDashboards.length - 1])
                    await this.$router.push('/')
                } catch (err) {
                    console.error(err)
                } finally {
                    this.loading = false
                    this.showConfirmDialog = false
                }
            },
            async onDelete() {
                this.showConfirmDialog = true
            },
            async onChangeLayout(layout) {
                this.model['DashboardLayoutID'] = layout.LayoutID
                await this.$store.dispatch('dashboards/updateDashboard', this.model)
            },
            keepInitialLayout() {
                this.initialLayout = cloneDeep(this.activeLayout)
            },
            onUpdateGroups(groups) {
                this.currentDashboard.WidgetGroupList = groups
                this.model.WidgetGroupList = groups
            },
        },
        mounted() {
            this.keepInitialLayout()
        },
        watch: {
            activeDashboard: {
                immediate: true,
                deep: true,
                handler(dashboard) {
                    this.model = cloneDeep(dashboard)
                    this.currentDashboard = cloneDeep(dashboard)
                },
            },
        },
    }
</script>

<style lang="scss" scoped>
.dashboard-settings {
    background: var(--mainBgColor-color);
}
.under-header-block {
    @apply border-b border-gray-300;
}
</style>
